import React, { useState } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  UserCheck,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import resourceRoleMasterService from '../../services/resourceRoleMasterService'

const ResourceRoleMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [formData, setFormData] = useState({
    resourceRoleCode: '',
    resourceRoleName: '',
    resourceRoleDescription: '',
    roleCategory: 'Technical',
    roleLevel: 'Mid-level',
    resourceRoleStatus: 'Active',
    requiredSkills: [],
    preferredSkills: [],
    minimumExperience: 0,
    maximumExperience: 5,
    educationRequirements: [],
    certifications: [],
    workType: 'Full-time',
    workLocation: 'Hybrid',
    availability: 'Immediate',
    salaryRange: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'Monthly'
    },
    benefits: [],
    reportsTo: '',
    managesRoles: [],
    teamSize: {
      min: 0,
      max: 0
    },
    suitableProjectTypes: [],
    suitableProjectSizes: [],
    billableRate: 0,
    utilizationTarget: 80,
    careerPath: []
  })

  const [loading, setLoading] = useState(false)

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.resourceRoleCode.trim()) {
      toast.error('Resource Role Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.resourceRoleCode.trim())) {
      toast.error('Resource Role Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.resourceRoleName.trim()) {
      toast.error('Resource Role Name is required')
      return false
    }
    if (!formData.resourceRoleDescription.trim()) {
      toast.error('Resource Role Description is required')
      return false
    }
    if (formData.minimumExperience < 0) {
      toast.error('Minimum Experience cannot be negative')
      return false
    }
    if (formData.maximumExperience < formData.minimumExperience) {
      toast.error('Maximum Experience must be greater than or equal to Minimum Experience')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Resource Role Master record...')
      const response = await resourceRoleMasterService.createResourceRoleMaster({
        resourceRoleCode: formData.resourceRoleCode.toUpperCase(),
        resourceRoleName: formData.resourceRoleName,
        resourceRoleDescription: formData.resourceRoleDescription,
        roleCategory: formData.roleCategory,
        roleLevel: formData.roleLevel,
        resourceRoleStatus: formData.resourceRoleStatus,
        requiredSkills: formData.requiredSkills,
        preferredSkills: formData.preferredSkills,
        minimumExperience: formData.minimumExperience,
        maximumExperience: formData.maximumExperience,
        educationRequirements: formData.educationRequirements,
        certifications: formData.certifications,
        workType: formData.workType,
        workLocation: formData.workLocation,
        availability: formData.availability,
        salaryRange: formData.salaryRange,
        benefits: formData.benefits,
        reportsTo: formData.reportsTo,
        managesRoles: formData.managesRoles,
        teamSize: formData.teamSize,
        suitableProjectTypes: formData.suitableProjectTypes,
        suitableProjectSizes: formData.suitableProjectSizes,
        billableRate: formData.billableRate,
        utilizationTarget: formData.utilizationTarget,
        careerPath: formData.careerPath
      })

      if (response.success) {
        toast.success('Resource Role Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/resource-role-master')
      } else {
        toast.error(response.message || 'Failed to create Resource Role Master record')
      }
    } catch (error) {
      console.error('Error creating Resource Role Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Resource Role Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Resource Role Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      resourceRoleCode: '',
      resourceRoleName: '',
      resourceRoleDescription: '',
      roleCategory: 'Technical',
      roleLevel: 'Mid-level',
      resourceRoleStatus: 'Active',
      requiredSkills: [],
      preferredSkills: [],
      minimumExperience: 0,
      maximumExperience: 5,
      educationRequirements: [],
      certifications: [],
      workType: 'Full-time',
      workLocation: 'Hybrid',
      availability: 'Immediate',
      salaryRange: {
        min: 0,
        max: 0,
        currency: 'INR',
        period: 'Monthly'
      },
      benefits: [],
      reportsTo: '',
      managesRoles: [],
      teamSize: {
        min: 0,
        max: 0
      },
      suitableProjectTypes: [],
      suitableProjectSizes: [],
      billableRate: 0,
      utilizationTarget: 80,
      careerPath: []
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/resource-role-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Resource Role Master', path: '/projects/resource-role-master', onClick: () => navigate('/projects/resource-role-master') },
    { label: 'Create New', path: '/projects/resource-role-master/new', isCurrent: true }
  ]

  const categoryOptions = [
    'Technical',
    'Management',
    'Design',
    'Quality Assurance',
    'DevOps',
    'Business',
    'Support',
    'Other'
  ]

  const levelOptions = [
    'Junior',
    'Mid-level',
    'Senior',
    'Lead',
    'Principal',
    'Architect',
    'Manager',
    'Director'
  ]

  const statusOptions = [
    'Active',
    'Inactive',
    'Deprecated',
    'Legacy'
  ]

  const workTypeOptions = [
    'Full-time',
    'Part-time',
    'Contract',
    'Freelance',
    'Intern',
    'Consultant'
  ]

  const workLocationOptions = [
    'On-site',
    'Remote',
    'Hybrid',
    'Any'
  ]

  const availabilityOptions = [
    'Immediate',
    '2 Weeks',
    '1 Month',
    '2 Months',
    '3 Months',
    'Negotiable'
  ]

  const currencyOptions = [
    'INR',
    'USD',
    'EUR',
    'GBP'
  ]

  const periodOptions = [
    'Hourly',
    'Daily',
    'Monthly',
    'Annually',
    'Project-based'
  ]

  const projectTypeOptions = [
    'Web Development',
    'Mobile Development',
    'Desktop Application',
    'API Development',
    'Data Science',
    'AI/ML',
    'DevOps',
    'Cloud Migration',
    'Legacy Modernization'
  ]

  const projectSizeOptions = [
    'Small',
    'Medium',
    'Large',
    'Enterprise'
  ]

  const helpContent = (
    <>
      <h3>What is Resource Role Master?</h3>
      <p>Resource Role Master lets you <strong>define roles</strong> such as Developer, Designer, PM, QA, and DevOps. You can set category, level, required/preferred skills, experience range, work type/location, salary range, billable rate, and suitable project types.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Role code, name, description, category, level, status, required/preferred skills, experience range, education, certifications.</p>
      <p><strong>Work &amp; Compensation</strong> – Work type, location, availability, salary range, benefits, reports to, manages roles, team size.</p>
      <p><strong>Project &amp; Utilization</strong> – Suitable project types/sizes, billable rate, utilization target, career path.</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Resource Role Master Entry</h1>
          <p>Create and manage resource role information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Resource Role Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <UserCheck size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            <Settings size={18} />
            <span>Skills & Experience</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'work' ? 'active' : ''}`}
            onClick={() => setActiveTab('work')}
          >
            <Calendar size={18} />
            <span>Work Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'compensation' ? 'active' : ''}`}
            onClick={() => setActiveTab('compensation')}
          >
            <DollarSign size={18} />
            <span>Compensation</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'career' ? 'active' : ''}`}
            onClick={() => setActiveTab('career')}
          >
            <Users size={18} />
            <span>Career Path</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Resource Role Code, Resource Role Name, Category, Level, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="resourceRoleCode" className="form-label required">
                    Resource Role Code
                  </label>
                  <input
                    id="resourceRoleCode"
                    type="text"
                    value={formData.resourceRoleCode}
                    onChange={(e) => handleInputChange('resourceRoleCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., DEV-SR, PM-LEAD"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="resourceRoleName" className="form-label required">
                    Resource Role Name
                  </label>
                  <input
                    id="resourceRoleName"
                    type="text"
                    value={formData.resourceRoleName}
                    onChange={(e) => handleInputChange('resourceRoleName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Senior Developer"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="roleCategory" className="form-label required">
                    Role Category
                  </label>
                  <select
                    id="roleCategory"
                    value={formData.roleCategory}
                    onChange={(e) => handleInputChange('roleCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="roleLevel" className="form-label required">
                    Role Level
                  </label>
                  <select
                    id="roleLevel"
                    value={formData.roleLevel}
                    onChange={(e) => handleInputChange('roleLevel', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Level</option>
                    {levelOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Status */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="resourceRoleStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="resourceRoleStatus"
                    value={formData.resourceRoleStatus}
                    onChange={(e) => handleInputChange('resourceRoleStatus', e.target.value)}
                    className="form-input"
                  >
                    {statusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Resource Role Description */}
              <div className="form-group">
                <label htmlFor="resourceRoleDescription" className="form-label required">
                  Resource Role Description
                </label>
                <textarea
                  id="resourceRoleDescription"
                  value={formData.resourceRoleDescription}
                  onChange={(e) => handleInputChange('resourceRoleDescription', e.target.value)}
                  className="form-input"
                  placeholder="Describe the role responsibilities and requirements..."
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {/* Skills & Experience Tab */}
          {activeTab === 'skills' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Skills & Experience Information</h3>
              </div>
              
              {/* Row 1: Experience Range */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="minimumExperience" className="form-label">
                    Minimum Experience (Years)
                  </label>
                  <input
                    id="minimumExperience"
                    type="number"
                    value={formData.minimumExperience}
                    onChange={(e) => handleInputChange('minimumExperience', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="50"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="maximumExperience" className="form-label">
                    Maximum Experience (Years)
                  </label>
                  <input
                    id="maximumExperience"
                    type="number"
                    value={formData.maximumExperience}
                    onChange={(e) => handleInputChange('maximumExperience', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="50"
                  />
                </div>
              </div>

              {/* Row 2: Work Type, Work Location, Availability */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="workType" className="form-label">
                    Work Type
                  </label>
                  <select
                    id="workType"
                    value={formData.workType}
                    onChange={(e) => handleInputChange('workType', e.target.value)}
                    className="form-input"
                  >
                    {workTypeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="workLocation" className="form-label">
                    Work Location
                  </label>
                  <select
                    id="workLocation"
                    value={formData.workLocation}
                    onChange={(e) => handleInputChange('workLocation', e.target.value)}
                    className="form-input"
                  >
                    {workLocationOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="availability" className="form-label">
                    Availability
                  </label>
                  <select
                    id="availability"
                    value={formData.availability}
                    onChange={(e) => handleInputChange('availability', e.target.value)}
                    className="form-input"
                  >
                    {availabilityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Work Details Tab */}
          {activeTab === 'work' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Work Details & Reporting</h3>
              </div>
              
              {/* Row 1: Reports To, Billable Rate, Utilization Target */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="reportsTo" className="form-label">
                    Reports To
                  </label>
                  <input
                    id="reportsTo"
                    type="text"
                    value={formData.reportsTo}
                    onChange={(e) => handleInputChange('reportsTo', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Tech Lead, Manager"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="billableRate" className="form-label">
                    Billable Rate ($/hour)
                  </label>
                  <input
                    id="billableRate"
                    type="number"
                    value={formData.billableRate}
                    onChange={(e) => handleInputChange('billableRate', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="utilizationTarget" className="form-label">
                    Utilization Target (%)
                  </label>
                  <input
                    id="utilizationTarget"
                    type="number"
                    value={formData.utilizationTarget}
                    onChange={(e) => handleInputChange('utilizationTarget', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                    placeholder="80"
                  />
                </div>
              </div>

              {/* Row 2: Team Size */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="teamSizeMin" className="form-label">
                    Team Size (Min)
                  </label>
                  <input
                    id="teamSizeMin"
                    type="number"
                    value={formData.teamSize.min}
                    onChange={(e) => handleInputChange('teamSize', { ...formData.teamSize, min: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="teamSizeMax" className="form-label">
                    Team Size (Max)
                  </label>
                  <input
                    id="teamSizeMax"
                    type="number"
                    value={formData.teamSize.max}
                    onChange={(e) => handleInputChange('teamSize', { ...formData.teamSize, max: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Compensation Tab */}
          {activeTab === 'compensation' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Compensation & Benefits</h3>
              </div>
              
              {/* Row 1: Salary Range */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="salaryMin" className="form-label">
                    Min Salary
                  </label>
                  <input
                    id="salaryMin"
                    type="number"
                    value={formData.salaryRange.min}
                    onChange={(e) => handleInputChange('salaryRange', { ...formData.salaryRange, min: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="salaryMax" className="form-label">
                    Max Salary
                  </label>
                  <input
                    id="salaryMax"
                    type="number"
                    value={formData.salaryRange.max}
                    onChange={(e) => handleInputChange('salaryRange', { ...formData.salaryRange, max: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="salaryCurrency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="salaryCurrency"
                    value={formData.salaryRange.currency}
                    onChange={(e) => handleInputChange('salaryRange', { ...formData.salaryRange, currency: e.target.value })}
                    className="form-input"
                  >
                    {currencyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="salaryPeriod" className="form-label">
                    Period
                  </label>
                  <select
                    id="salaryPeriod"
                    value={formData.salaryRange.period}
                    onChange={(e) => handleInputChange('salaryRange', { ...formData.salaryRange, period: e.target.value })}
                    className="form-input"
                  >
                    {periodOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Career Path Tab */}
          {activeTab === 'career' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Career Progression</h3>
                <p>Define potential career advancement paths for this role</p>
              </div>
              
              <div className="form-group">
                <label className="form-label">Career Path Configuration</label>
                
                {/* Career Path List */}
                <div className="resource-roles-container" style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  minHeight: '200px'
                }}>
                  {formData.careerPath.length === 0 ? (
                    <div className="empty-state" style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      color: '#64748b'
                    }}>
                      <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>No Career Path Added</h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>Add career progression paths for this role</p>
                      <button
                        type="button"
                        onClick={() => addArrayItem('careerPath', { nextRole: '', timeToPromotion: 0, requirements: '' })}
                        className="btn-primary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        <Plus size={16} style={{ marginRight: '8px' }} />
                        Add First Path
                      </button>
                    </div>
                  ) : (
                    <div className="resource-roles-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.careerPath.map((path, index) => (
                        <div key={index} className="resource-role-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="role-number" style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#292752',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          
                          <input
                            type="text"
                            value={path.nextRole}
                            onChange={(e) => {
                              const newPaths = [...formData.careerPath]
                              newPaths[index] = { ...newPaths[index], nextRole: e.target.value }
                              handleArrayChange('careerPath', newPaths)
                            }}
                            className="form-input"
                            placeholder="Next role (e.g., Tech Lead, Senior Manager)"
                            style={{ 
                              flex: 1,
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          
                          <input
                            type="number"
                            value={path.timeToPromotion}
                            onChange={(e) => {
                              const newPaths = [...formData.careerPath]
                              newPaths[index] = { ...newPaths[index], timeToPromotion: parseInt(e.target.value) || 0 }
                              handleArrayChange('careerPath', newPaths)
                            }}
                            className="form-input"
                            placeholder="Months"
                            style={{ 
                              width: '100px',
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          
                          <button
                            type="button"
                            onClick={() => removeArrayItem('careerPath', index)}
                            className="btn-danger-small"
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Path Button */}
                  {formData.careerPath.length > 0 && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => addArrayItem('careerPath', { nextRole: '', timeToPromotion: 0, requirements: '' })}
                        className="btn-secondary"
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Plus size={16} />
                        Add Another Path
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Help Text */}
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>💡</span>
                  <span>Common paths: Junior → Mid-level → Senior → Lead → Principal</span>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Resource Role'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Resource Role Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
};

export default ResourceRoleMasterForm;
